import { ReactNode, useState } from "react";

import { IconType } from "./types";
import { useTheme } from "@mui/material/styles";
import { getPaleteColor } from "./utils";
import Flex from "../flex";

import arrowDown from "./svg/arrow-down.svg";
import arrowDownFromBracketThin from "./svg/arrow-down-to-bracket-thin.svg";
import arrowUp from "./svg/arrow-up.svg";
import arrowUpFromBracketThin from "./svg/arrow-up-from-bracket-thin.svg";
import arrowUpFromRight from "./svg/arrow-up-right-from-square.svg";
import bookSparkles from "./svg/book-sparkles-light.svg";
import barsFilter from "./svg/bars-filter.svg";
import bolt from "./svg/bolt.svg";
import boltLight from "./svg/bolt-thin.svg";
import boxArchive from "./svg/box-archive.svg";
import calendarLight from "./svg/calendar-light.svg";
import caretDown from "./svg/caret-down.svg";
import cartShopping from "./svg/cart-shopping.svg";
import cartShoppingRegular from "./svg/cart-shopping-regular.svg";
import chartUser1Duo from "./svg/chart-user-1-duo.svg";
import chartMixed from "./svg/chart-mixed-thin.svg";
import chartLineUp from "./svg/chart-line-up-light.svg";
import chevronRight from "./svg/chevron-right.svg";
import chevronLeft from "./svg/chevron-left.svg";
import check from "./svg/check.svg";
import circle from "./svg/circle-light.svg";
import circleArrowUp from "./svg/circle-arrow-up.svg";
import circleCheck from "./svg/circle-check.svg";
import circleCheckFilled from "./svg/circle-check-solid.svg";
import CircleCheckThick from "./svg/circle-check-thick.svg";
import CircleDot from "./svg/circle-dot-light.svg";
import circleInfo from "./svg/circle-info.svg";
import circlePlus from "./svg/circle-plus-light.svg";
import circlePlusFilled from "./svg/circle-plus-solid.svg";
import circlePlusThin from "./svg/circle-plus-thin.svg";
import circleMinus from "./svg/circle-minus-light.svg";
import circleXMark from "./svg/circle-xmark.svg";
import circleXMarkSolid from "./svg/circle-xmark-solid.svg";
import clipboard from "./svg/clipboard.svg";
import clone from "./svg/clone.svg";
import comment from "./svg/comment.svg";
import commentSlash from "./svg/comment-slash.svg";
import commentLines from "./svg/comment-lines.svg";
import demote from "./svg/demote.svg";
import downToBracket from "./svg/down-to-bracket.svg";
import ellipsisVertical from "./svg/ellipsis-vertical.svg";
import empty from "./svg/empty.svg";
import emptyThin from "./svg/empty-thin.svg";
import fileCSVLight from "./svg/file-csv-light.svg";
import fillDrip from "./svg/fill-drip-light.svg";
import gear from "./svg/gear.svg";
import genAdBot from "./svg/gen-ad-bot.svg";
import house from "./svg/house-light.svg";
import lLight from "./svg/l-light.svg";
import layerGroup from "./svg/layer-group.svg";
import lightbulbOn from "./svg/lightbulb-on.svg";
import lightbulbOnLight from "./svg/lightbulb-on-light.svg";
import lineColumnsLight from "./svg/line-columns-light.svg";
import loading from "./svg/loading.svg";
import loader from "./svg/loader.svg";
import messageImageDuo from "./svg/message-image-duo.svg";
import messageImageLight from "./svg/message-image-light.svg";
import moneyCheckPen from "./svg/money-check-pen.svg";
import paintbrushPencil from "./svg/paintbrush-pencil.svg";
import pencil from "./svg/pencil.svg";
import penCircleSolid from "./svg/pen-circle-solid.svg";
import penToSquare from "./svg/pen-to-square.svg";
import plus from "./svg/plus.svg";
import plusCustom from "./svg/plus-custom.svg";
import redoRegular from "./svg/redo-regular.svg";
import sliders from "./svg/sliders.svg";
import sparkles from "./svg/sparkles.svg";
import squareMinus from "./svg/square-minus-solid.svg";
import starEmpty from "./svg/star-empty.svg";
import starEmptyThin from "./svg/star-empty-thin.svg";
import starFilled from "./svg/star-filled.svg";
import text from "./svg/text.svg";
import thumbsUp from "./svg/thumbs-up.svg";
import thunmbsDown from "./svg/thumbs-down.svg";
import trashCan from "./svg/trash-can.svg";
import trashUndo from "./svg/trash-undo.svg";
import triangleExclamation from "./svg/triangle-exclamation.svg";
import upFromBracket from "./svg/up-from-bracket.svg";
import undoRegular from "./svg/undo-regular.svg";
import xmark from "./svg/xmark.svg";
import GDNColor from "./svg/GDN-Color.svg";
import GDNGray from "./svg/GDN-Gray.svg";
import MSANColor from "./svg/MSAN-Color.svg";
import MSANGray from "./svg/MSAN-Gray.svg";
import MetaColor from "./svg/Meta-Color.svg";
import MetaGray from "./svg/Meta-Gray.svg";
import Meta from "./svg/Meta.svg";
import link from "./svg/link.svg";
import CircleExclamation from "./svg/circle-exclamation.svg";
import ShieldHalved from "./svg/shield-halved.svg";
import TrophyStar from "./svg/trophy-star-duotone-thin.svg";
import video from "./svg/video-arrow-up-right.svg";
import ClipboardListCheckDuo from "./svg/clipboard-list-check-duo.svg";
import ImagePolaroidDuo from "./svg/image-polaroid-duo.svg";
import SparklesDuo from "./svg/sparkles-duo.svg";
import TrophyStarDuo from "./svg/trophy-star-duo.svg";
import radioSelected from "./svg/radio_selected.svg";
import radioUnselected from "./svg/radio_unselected.svg";

type SVGImportType = (props: React.SVGProps<SVGSVGElement>) => ReactNode | Promise<ReactNode>;

// NOTE: Please keep these in alphabetical order
const IconMap: Record<IconType, SVGImportType> = {
  [IconType.ArrowDown]: arrowDown,
  [IconType.ArrowDownToBracketThin]: arrowDownFromBracketThin,
  [IconType.ArrowUp]: arrowUp,
  [IconType.ArrowUpFromBracketThin]: arrowUpFromBracketThin,
  [IconType.ArrowUpRightFromSquare]: arrowUpFromRight,
  [IconType.BookSparkles]: bookSparkles,
  [IconType.BarsFilter]: barsFilter,
  [IconType.Bolt]: bolt,
  [IconType.BoltLight]: boltLight,
  [IconType.BoxArchive]: boxArchive,
  [IconType.CalendarLight]: calendarLight,
  [IconType.CaretDown]: caretDown,
  [IconType.CartShopping]: cartShopping,
  [IconType.CartShoppingRegular]: cartShoppingRegular,
  [IconType.ChartLineUp]: chartLineUp,
  [IconType.ChevronLeft]: chevronLeft,
  [IconType.ChevronRight]: chevronRight,
  [IconType.ChartUser1Duo]: chartUser1Duo,
  [IconType.ChartMixed]: chartMixed,
  [IconType.Check]: check,
  [IconType.Circle]: circle,
  [IconType.CircleArrowUp]: circleArrowUp,
  [IconType.CircleCheck]: circleCheck,
  [IconType.CircleCheckFilled]: circleCheckFilled,
  [IconType.CircleCheckThick]: CircleCheckThick,
  [IconType.CircleDot]: CircleDot,
  [IconType.CircleExclamation]: CircleExclamation,
  [IconType.CircleInfo]: circleInfo,
  [IconType.CirclePlus]: circlePlus,
  [IconType.CirclePlusFilled]: circlePlusFilled,
  [IconType.CirclePlusThin]: circlePlusThin,
  [IconType.CircleMinus]: circleMinus,
  [IconType.CircleXMark]: circleXMark,
  [IconType.CircleXMarkSolid]: circleXMarkSolid,
  [IconType.Clipboard]: clipboard,
  [IconType.ClipboardListCheckDuo]: ClipboardListCheckDuo,
  [IconType.Clone]: clone,
  [IconType.Comment]: comment,
  [IconType.CommentSlash]: commentSlash,
  [IconType.CommentLines]: commentLines,
  [IconType.Demote]: demote,
  [IconType.DownToBracket]: downToBracket,
  [IconType.EllipsisVertical]: ellipsisVertical,
  [IconType.Empty]: empty,
  [IconType.EmptyThin]: emptyThin,
  [IconType.FileCSVLight]: fileCSVLight,
  [IconType.FillDrip]: fillDrip,
  [IconType.Gear]: gear,
  [IconType.GenAdBot]: genAdBot,
  [IconType.GDNColor]: GDNColor,
  [IconType.GDNGray]: GDNGray,
  [IconType.House]: house,
  [IconType.LLight]: lLight,
  [IconType.LayerGroup]: layerGroup,
  [IconType.LightbulbOn]: lightbulbOn,
  [IconType.LightbulbOnLight]: lightbulbOnLight,
  [IconType.LineColumnsLight]: lineColumnsLight,
  [IconType.Loading]: loading,
  [IconType.Loader]: loader,
  [IconType.MessageImageLight]: messageImageLight,
  [IconType.MessageImageDuo]: messageImageDuo,
  [IconType.MetaColor]: MetaColor,
  [IconType.MetaGray]: MetaGray,
  [IconType.Meta]: Meta,
  [IconType.MoneyCheckPen]: moneyCheckPen,
  [IconType.MSANColor]: MSANColor,
  [IconType.MSANGray]: MSANGray,
  [IconType.PaintbrushPencil]: paintbrushPencil,
  [IconType.Pencil]: pencil,
  [IconType.PenCircleSolid]: penCircleSolid,
  [IconType.PenToSquare]: penToSquare,
  [IconType.Plus]: plus,
  [IconType.PlusCustom]: plusCustom,
  [IconType.RedoRegular]: redoRegular,
  [IconType.ShieldHalved]: ShieldHalved,
  [IconType.Sliders]: sliders,
  [IconType.Sparkles]: sparkles,
  [IconType.SquareMinus]: squareMinus,
  [IconType.StarEmpty]: starEmpty,
  [IconType.StarEmptyThin]: starEmptyThin,
  [IconType.StarFilled]: starFilled,
  [IconType.Text]: text,
  [IconType.ThumbsDown]: thunmbsDown,
  [IconType.ThumbsUp]: thumbsUp,
  [IconType.TrashCan]: trashCan,
  [IconType.TrashUndo]: trashUndo,
  [IconType.TriangleExclamation]: triangleExclamation,
  [IconType.UpFromBracket]: upFromBracket,
  [IconType.Xmark]: xmark,
  [IconType.Link]: link,
  [IconType.UndoRegular]: undoRegular,
  [IconType.TrophyStar]: TrophyStar,
  [IconType.Video]: video,
  [IconType.ImagePolaroidDuo]: ImagePolaroidDuo,
  [IconType.SparklesDuo]: SparklesDuo,
  [IconType.TrophyStarDuo]: TrophyStarDuo,
  [IconType.RadioSelected]: radioSelected,
  [IconType.RadioUnselected]: radioUnselected
};

export interface IconProps {
  name: IconType;
  height?: number | string;
  width?: number | string;
  color?: string | "none";
  hoverColor?: string;
  isButtonHovered?: boolean;
}

function Icon({
  name,
  height = 20,
  width = 20,
  color,
  hoverColor,
  isButtonHovered = false
}: IconProps): React.JSX.Element | null {
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const theme = useTheme();
  const IconComponent = IconMap[name];

  if (!IconComponent) {
    console.error(`Icon ${name} not found in IconMap`);
    return null;
  }

  const fillColor = getPaleteColor(theme, color);
  const hoverFillColor = getPaleteColor(theme, hoverColor);

  // Only add mouse events if hoverFillColor is provided
  const hoverProps = hoverFillColor
    ? {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave
      }
    : {};

  return IconComponent ? (
    <Flex {...hoverProps}>
      <IconComponent
        height={height}
        width={width}
        role="img"
        fill={(isButtonHovered || isHovered) && hoverFillColor ? hoverFillColor : fillColor}
        style={hoverFillColor ? { transition: "fill 0.3s ease" } : undefined}
      />
    </Flex>
  ) : null;
}

export default Icon;

export { IconType } from "./types";
